import type { Fields } from '@tribeplatform/gql-client/types'
import {
  MassActionRequest,
  MassActionRequestContext,
  MassActionRequestStatus,
  QueryMassActionRequestArgs,
} from '@tribeplatform/gql-client/types'

import { useQuery, UseQueryOptions } from '../../lib/react-query/useQuery.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getMemberInvitationsKey } from '../../utils/keys/authToken.keys.js'
import { getMassActionRequestKey } from '../../utils/keys/massAction.keys.js'
import { getMembersKey } from '../../utils/keys/member.key.js'
import {
  getModerationItemsKey,
  getPostModerationItemsKey,
} from '../../utils/keys/moderation.key.js'
import { getPostsKey } from '../../utils/keys/post.key.js'

export const useMassActionRequest = (options: {
  variables: QueryMassActionRequestArgs
  fields?: Fields.MassActionRequestFields
  useQueryOptions?: UseQueryOptions<MassActionRequest>
}) => {
  const { variables, fields = 'basic', useQueryOptions } = options
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const massActionRequestKey = getMassActionRequestKey({ variables, fields })

  return useQuery<MassActionRequest>(
    massActionRequestKey,
    () =>
      client.query({
        name: 'massActionRequest',
        args: {
          fields,
          variables,
        },
      }),
    {
      ...useQueryOptions,
      onSuccess: massActionRequest => {
        if (massActionRequest?.status === MassActionRequestStatus.Done) {
          if (massActionRequest?.context === MassActionRequestContext.Member) {
            queryClient.invalidateQueries(getMembersKey())
          }
          if (
            massActionRequest?.context ===
            MassActionRequestContext.MemberInvitation
          ) {
            queryClient.invalidateQueries(getMemberInvitationsKey())
          }
          if (massActionRequest?.context === MassActionRequestContext.Post) {
            queryClient.invalidateQueries(getPostsKey())
            queryClient.invalidateQueries(getModerationItemsKey())
            queryClient.invalidateQueries(getPostModerationItemsKey())
          }
        }
        useQueryOptions?.onSuccess?.(massActionRequest)
      },
    },
  )
}
